import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { ExternalEventForm } from '../components/events/ExternalEventForm';
import type { ExternalEventFormValues } from '../components/events/ExternalEventForm';
import {
  useCreateEventDraft,
  useCreateEvent,
  type ExternalEventCreationPayload,
  type MediaUrlEntry,
} from '../api/events';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { resolveDominantColorForSubmit } from '../utils/dominantColor';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from '../utils/toast';
import { saveEventSuccess } from '../utils/eventSuccess';
import { extractApiError } from '../utils/apiError';
import { wallClockAsPicked } from '../utils/eventDate';

export const Route = createFileRoute('/events/create/external')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: CreateExternalEventPage,
});

function CreateExternalEventPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createDraft = useCreateEventDraft();
  const createEvent = useCreateEvent();
  const handleSubmit = async (values: ExternalEventFormValues, action: 'save' | 'publish') => {
    const start = wallClockAsPicked(values.startDate, values.startTime);
    const end = wallClockAsPicked(values.endDate, values.endTime);

    const eventPayload: ExternalEventCreationPayload = {
      event_title: values.event_title,
      event_type: 'physical',
      location_body: values.locationBody
        ? {
            street: values.locationBody.address,
            city: values.locationBody.city,
            state: values.locationBody.state,
            country: values.locationBody.country,
            longitude: values.locationBody.longitude,
            latitude: values.locationBody.latitude,
          }
        : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 },
      start,
      end,
      address:
        values.locationDetails || values.locationBody?.address || values.locationBody?.city || '',
      collaborators: values.collaboratorIds.length ? values.collaboratorIds : undefined,
      external_ticketing_url: values.external_ticketing_url ?? '',
      event_ticketing_type: 'external',
      iana_timezone: values.timezone,
      info: values.info,
      dominant_color: await resolveDominantColorForSubmit(values.images[0], values.dominantColor),
    };

    // Upload new images to Cloudinary
    let mediaUrls: MediaUrlEntry[] = [];
    const newFiles = values.imageFiles.filter((f): f is File => f !== null);
    if (newFiles.length > 0) {
      try {
        const results = await uploadFilesToCloudinary(newFiles, {
          folder: 'event_images',
          uploadPreset: 'event_preset',
        });
        mediaUrls = results.map((r) => ({
          public_id: r.public_id,
          secure_url: r.secure_url,
          resource_type: r.resource_type,
          bytes: r.bytes,
          format: r.format,
        }));
      } catch {
        toast.error('Failed to upload images. Please try again.');
        return;
      }
    }

    const payload = {
      event: eventPayload,
      ...(mediaUrls.length > 0 ? { media_urls: mediaUrls } : {}),
    };

    if (action === 'publish') {
      createEvent.mutate(payload, {
        onSuccess: (data) => {
          toast.success('External event created successfully!');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          const successId = saveEventSuccess(
            data,
            values.locationBody ?? null,
            mediaUrls[0]?.secure_url,
          );
          if (successId) {
            navigate({
              to: '/events/$eventId/success',
              params: { eventId: successId },
              replace: true,
            });
          }
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create external event'));
        },
      });
    } else {
      createDraft.mutate(payload, {
        onSuccess: (data) => {
          toast.success('External event draft saved!');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          const eventId = data.event_id ?? data.event?.id;
          if (eventId) {
            navigate({
              to: '/events/$eventId',
              params: { eventId: String(eventId) },
              replace: true,
            });
          }
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create external event'));
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20">
        <div className="max-w-360 mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>

        <ExternalEventForm
          mode="create"
          title="New External Event"
          statusBadge="draft"
          onSubmit={handleSubmit}
          isLoading={createDraft.isPending}
        />
      </main>

      <BottomNav />
    </div>
  );
}
